import {createAsyncThunk} from '@reduxjs/toolkit'
import {api2Endpoint} from 'app/config/apiConfig'
import {deleteData} from 'app/hooks/api/useDeleteData'
import {getData} from 'app/hooks/api/useGetData'
import {postData} from 'app/hooks/api/usePostData'
import {putData} from 'app/hooks/api/usePutData'
import {postUserFileToS3} from 'app/hooks/user_files/postUserFileToS3'
import {IUserFile} from 'app/models/user_file'
import {INoteFE} from 'interfaces/INoteFE'

export const fetchNoteListForEntity = createAsyncThunk(
  'noteList/fetchNoteListForEntity',
  async (args: {entityType: string; entityId: string}) => {
    const {entityType, entityId} = args
    try {
      const response = await getData<INoteFE>(
        api2Endpoint(`std/graph_entity/${entityType}/${entityId}/notes`)
      )
      if (!response) {
        throw new Error('Failed to fetch saved search')
      }
      if (response) {
        return {key: entityType + '|' + entityId, notes: response as INoteFE[]}
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const fetchNoteListForProject = createAsyncThunk(
  'noteList/fetchNoteListForProject',
  async (args: {project_id: string}) => {
    const {project_id} = args
    try {
      const response = await getData<INoteFE>(api2Endpoint(`std/project/${project_id}/notes`))
      if (!response) {
        throw new Error('Failed to fetch project notes')
      }
      if (response) {
        return {key: project_id, notes: response as INoteFE[]}
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const createAddToNoteList = createAsyncThunk(
  'noteList/updateNoteList',
  async (args: {data: any; entityType?: string; entityId?: string; projectId?: string}) => {
    const {data, entityType, entityId, projectId} = args
    const route = projectId
      ? `std/project/${projectId}/notes`
      : `std/graph_entity/${entityType}/${entityId}/notes`
    try {
      const response = await postData<unknown>(api2Endpoint(route), data, undefined)

      if (!response) {
        throw new Error('Failed to create note list')
      }
      //response is the created note
      if (response) {
        const key = projectId ? projectId : entityType + '|' + entityId
        return {key: key, note: response as INoteFE}
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const AddAttachmentNoteToList = createAsyncThunk(
  'noteList/createAddNoteWithAttachmentToList',
  async (args: {
    data: any
    fileObj: File
    note?: INoteFE
    entityType?: string
    entityId?: string
    projectId?: string
  }) => {
    const {data, fileObj, note, entityType, entityId, projectId} = args

    const route = projectId
      ? `std/project/${projectId}/notes`
      : `std/graph_entity/${entityType}/${entityId}/notes`
    try {
      let attachmentRes = null
      let newNote = {...note}
      let isUpdate = false
      if (note && note.id) {
        //we do an update
        isUpdate = true
        const body = {name: fileObj.name}
        attachmentRes = await postData<IUserFile>(
          api2Endpoint(`std/note/${note.id}/attachments`),
          body,
          undefined
        )
      } else {
        //we do create note then create attachment
        newNote = await postData<any>(api2Endpoint(route), data, undefined)
        if (newNote && newNote.id) {
          const body = {name: fileObj.name}
          attachmentRes = await postData<IUserFile>(
            api2Endpoint(`std/note/${newNote.id}/attachments`),
            body,
            undefined
          )
        }
      }
      //post the attachement to s3
      if (attachmentRes?.id) {
        await postUserFileToS3(attachmentRes, fileObj)
      }
      // newNote is either passed in or created so we add the attachement to it then return as payload to reducx
      if (newNote) {
        newNote.attachments = newNote.attachments
          ? [...newNote.attachments, attachmentRes as IUserFile]
          : [attachmentRes as IUserFile]
      }

      const key = projectId ? projectId : entityType + '|' + entityId
      return {key: key, note: newNote, isUpdate: isUpdate}
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const updateNoteListItem = createAsyncThunk(
  'noteList/updateNoteListItem',
  async (args: {key: string; noteId: string; data: Partial<INoteFE>}) => {
    const {key, noteId, data} = args
    try {
      const response = await putData<any>(api2Endpoint(`std/note/${noteId}`), data)
      if (!response) {
        throw new Error('Failed to update note')
      } else {
        //if api doesnt return the note then need to update it in redux from here
        return {key: key, noteId: noteId, note: response}
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const deleteNotListItem = createAsyncThunk(
  'noteList/deleteNotListItem',
  async (args: {key: string; noteId: string}) => {
    try {
      const response = await deleteData(api2Endpoint(`std/note/${args.noteId}`))
      if (!response) {
        throw new Error('Failed to delete note')
      }
      //returns id of note that was deleted
      return {key: args.key, deletedNote: response}
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const deleteNoteListItemFileAttachement = createAsyncThunk(
  'noteList/deleteNotListItemAttachement',
  async (args: {key: string; fileId: string; note: INoteFE}) => {
    try {
      //api has no return for this, so relying on try catch to know if it was successful
      await deleteData(api2Endpoint(`std/user_file/${args.fileId}`))
      const newNote = {...args.note}
      newNote.attachments = newNote.attachments?.filter((f) => f.id !== args.fileId)
      return {key: args.key, noteId: args.note.id, deletedNote: newNote}
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)
